import Sucess from '@/assets/icons/Sucess';
import { useAuth } from '@/auth/Auth';
import { usePermissions } from '@/auth/PermissionsContext';
import { GenericModal } from '@/components/Onboarding/GenericModal';
import CustomSheet from '@/components/shared/Sheet/Sheet';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LayoutInside from '@/layout/LayoutInside';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { formatDateForAPI, getDefaultDate } from '@/utils/dateHelpers';
import { AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { useFetchOverdueReceivables } from './hooks/useFetchOverdueReceivables';
import { useFetchTaxes } from './hooks/useFetchTaxes';
import { useFetchTransactions } from './hooks/useFetchTransactions';
import { useFetchWalletData } from './hooks/useFetchWalletData';
import { useFetchWithdrawal } from './hooks/useFetchWithdrawal';
import AdvanceForm from './Partials/AdvanceForm';
import { FeesTab } from './Partials/FeesTab';
import ForecastHowItWorksModal, {
  FORECAST_HOW_IT_WORKS_MODAL_KEY,
} from './Partials/ForecastHowItWorksModal';
import PendingReceivablesModal, {
  PENDING_RECEIVABLES_MODAL_KEY,
  getSeenReceivablesCount,
} from './Partials/PendingReceivablesModal';
import StatementList from './Partials/StatementList';
import TransactionList from './Partials/TransactionList';
import WalletCards from './Partials/WalletCards';
import WithdrawalForm from './Partials/WithdrawalForm';
import WithdrawalList from './Partials/WithdrawalList';

const WALLET_TABS = {
  WITHDRAWALS: 'withdrawals',
  STATEMENT: 'statement',
  FORECAST: 'forecast',
  FEES: 'fees',
} as const;

const DEFAULT_TAB = WALLET_TABS.WITHDRAWALS;
const DEFAULT_FEE_TYPE = 'national';

export default function Wallet() {
  const navigate = useNavigate();
  const { t } = useTranslation('wallet');
  const { modals, openModal, closeModal } = useModalStore();
  const { authData } = useAuth();

  const { hasPermission, isPrimary } = usePermissions();
  const canViewWithdrawals = hasPermission('financial.view_withdrawals_list');
  const canViewStatement = hasPermission('financial.view_account_statement') || hasPermission('financial.view_transactions');
  const canViewForecast = hasPermission('financial.view_balance_forecast');
  const canViewFees = hasPermission('financial.view_fees_and_terms');
  const canRequestWithdrawal =
    isPrimary && hasPermission('wallet.request_normal_withdrawal');
  const canRequestAnticipation =
    isPrimary && hasPermission('wallet.request_anticipation');
  const canViewAvailable = hasPermission('wallet.view_available_balance');
  const canViewToRelease = hasPermission('wallet.view_balance_to_release');
  const canViewRetention = hasPermission('wallet.view_retention_balance');
  const canViewTotalWithdrawn = hasPermission('wallet.view_total_withdrawn');
  const canViewAnyWalletCard = canViewAvailable || canViewToRelease || canViewRetention || canViewTotalWithdrawn;

  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsString.withDefault(DEFAULT_TAB)
  );

  const [feeType, setFeeType] = useQueryState(
    'type',
    parseAsString.withDefault(DEFAULT_FEE_TYPE)
  );

  const [transactionsPage, setTransactionsPage] = useQueryState(
    'transactions_page',
    parseAsInteger.withDefault(1)
  );

  const [withdrawalsPage, setWithdrawalsPage] = useQueryState(
    'withdrawals_page',
    parseAsInteger.withDefault(1)
  );

  const [transactionsQuery, setTransactionsQuery] = useQueryState(
    'q',
    parseAsString.withDefault('')
  );

  const [transactionsStatus, setTransactionsStatus] = useQueryState(
    'status',
    parseAsString.withDefault('all')
  );

  const [startDate, setStartDate] = useQueryState('start_date', parseAsString);
  const [endDate, setEndDate] = useQueryState('end_date', parseAsString);

  const [dateRange, setDateRange] = useState<DateRange>({
    from: startDate ? new Date(startDate) : getDefaultDate(),
    to: endDate ? new Date(endDate) : getDefaultDate(),
  });

  const currencyValue = useCurrencyStore((s) => s.currency);
  const currency = typeof currencyValue === 'string' ? currencyValue : 'BRL';

  const [debouncedTransactionsQuery] = useDebounce(transactionsQuery, 500);

  const [localSeenHack, setLocalSeenHack] = useState(0);

  const { overdueReceivables } = useFetchOverdueReceivables({
    currency,
    enabled: canViewForecast && activeTab === WALLET_TABS.FORECAST,
  });

  const overdueTotalCount = overdueReceivables?.meta?.total ?? 0;

  const hasSeenReceivables = useMemo(() => {
    if (!overdueReceivables) return true; // prevent flashing yellow while loading
    if (overdueTotalCount === 0) return true;
    const seenCount = getSeenReceivablesCount();
    return seenCount === overdueTotalCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overdueReceivables, overdueTotalCount, localSeenHack]);

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      const formattedStart = formatDateForAPI(dateRange.from);
      const formattedEnd = formatDateForAPI(dateRange.to);
      setStartDate(formattedStart);
      setEndDate(formattedEnd);
    }
  }, [dateRange, setStartDate, setEndDate]);

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (range) {
        setDateRange(range);
        setTransactionsPage(1);
      }
    },
    [setTransactionsPage]
  );

  const handleFeeTypeChange = useCallback(
    (type: string) => {
      setFeeType(type);
    },
    [setFeeType]
  );

  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);

      if (tab === WALLET_TABS.STATEMENT) {
        setTransactionsPage(1);
        setWithdrawalsPage(null);
        if (!startDate || !endDate) {
          const today = getDefaultDate();
          setDateRange({ from: today, to: today });
        }
      } else if (tab === WALLET_TABS.WITHDRAWALS) {
        setWithdrawalsPage(1);
        setTransactionsPage(null);
        setTransactionsQuery(null);
        setTransactionsStatus(null);
        setStartDate(null);
        setEndDate(null);
      } else {
        setTransactionsPage(null);
        setWithdrawalsPage(null);
        setTransactionsQuery(null);
        setTransactionsStatus(null);
        setStartDate(null);
        setEndDate(null);
      }

      if (tab !== WALLET_TABS.FEES) {
        setFeeType(null);
      } else if (!feeType) {
        setFeeType(DEFAULT_FEE_TYPE);
      }
    },
    [
      setActiveTab,
      setFeeType,
      setTransactionsPage,
      setWithdrawalsPage,
      setTransactionsQuery,
      setTransactionsStatus,
      setStartDate,
      setEndDate,
      startDate,
      endDate,
      feeType,
    ]
  );

  const {
    transactions,
    isLoading: isLoadingTransactions,
    isRefetching: isRefetchingTransactions,
  } = useFetchTransactions({
    currentPage: transactionsPage,
    q: debouncedTransactionsQuery,
    status: transactionsStatus,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    enabled: canViewStatement,
    currency,
  });

  const {
    withdrawal,
    isLoading: isLoadingWithdrawal,
    isRefetching: isRefetchingWithdrawal,
  } = useFetchWithdrawal({
    currentPage: withdrawalsPage,
    enabled: canViewWithdrawals,
    currency,
  });

  const {
    retentionItem,
    receivableItem,
    totals,
    retention,
    isLoading: isLoadingWallet,
    anticipation,
  } = useFetchWalletData({
    currency,
    enabled: canViewAnyWalletCard,
  });

  const { taxes, isLoading: isLoadingTaxes } = useFetchTaxes({
    type: feeType || DEFAULT_FEE_TYPE,
    enabled: canViewFees && activeTab === WALLET_TABS.FEES,
    currency,
  });

  const walletData = useMemo(
    () => ({
      available:
        retention?.find((item) => item.type === 'available')?.total_formatted ??
        '',
      retention:
        retention?.find((item) => item.type === 'retention')?.total_formatted ??
        '',
      anticipation:
        retention?.find((item) => item.type === 'receivable')
          ?.total_formatted ?? '',
      totalWithdrawn: totals?.total_amount_formatted ?? '',
      isLoading: isLoadingWallet,
    }),
    [
      authData,
      retentionItem,
      receivableItem,
      totals,
      isLoadingWallet,
      retention,
    ]
  );

  const breadcrumbItems = useMemo(
    () => [{ text: t('financial'), link: '/financial' }],
    [t]
  );

  const tabs = useMemo(
    () => [
      ...(canViewWithdrawals ? [{ key: WALLET_TABS.WITHDRAWALS, label: t('withdrawals') }] : []),
      ...(canViewStatement ? [{ key: WALLET_TABS.STATEMENT, label: t('statement') }] : []),
      ...(canViewForecast ? [{ key: WALLET_TABS.FORECAST, label: t('forecast') }] : []),
      ...(canViewFees ? [{ key: WALLET_TABS.FEES, label: t('fees') }] : []),
    ],
    [t, canViewWithdrawals, canViewStatement, canViewForecast, canViewFees]
  );

  useEffect(() => {
    if (tabs.length === 0) return;
    const allowedKeys = tabs.map((tab) => tab.key);
    if (!allowedKeys.includes(activeTab as any)) {
      handleTabChange(allowedKeys[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const handleTransactionsPageChange = useCallback(
    (page: number) => {
      setTransactionsPage(page);
    },
    [setTransactionsPage]
  );

  const handleWithdrawalsPageChange = useCallback(
    (page: number) => {
      setWithdrawalsPage(page);
    },
    [setWithdrawalsPage]
  );

  const renderTabContent = useMemo(() => {
    switch (activeTab) {
      case WALLET_TABS.WITHDRAWALS:
        if (!canViewWithdrawals) return null;
        return (
          <WithdrawalList
            withdrawals={withdrawal}
            isLoading={isLoadingWithdrawal || isRefetchingWithdrawal}
            currentPage={withdrawalsPage}
            totalPages={withdrawal?.meta?.last_page || 1}
            onPageChange={handleWithdrawalsPageChange}
          />
        );

      case WALLET_TABS.STATEMENT:
        if (!canViewStatement) return null;
        return (
          <StatementList
            transactions={transactions}
            isLoading={isLoadingTransactions || isRefetchingTransactions}
            currentPage={transactionsPage}
            onPageChange={handleTransactionsPageChange}
            query={transactionsQuery}
            onQueryChange={setTransactionsQuery}
            status={transactionsStatus}
            onStatusChange={setTransactionsStatus}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            currency={currency}
          />
        );

      case WALLET_TABS.FORECAST:
        if (!canViewForecast) return null;
        return <TransactionList currency={currency} />;

      case WALLET_TABS.FEES:
        if (!canViewFees) return null;
        return (
          <FeesTab
            taxes={taxes?.taxes ?? []}
            reserved={taxes?.reserved}
            payouts={taxes?.payouts}
            withdrawal_fee={taxes?.withdrawal_fee}
            isLoading={isLoadingTaxes}
            feeType={feeType || DEFAULT_FEE_TYPE}
            onFeeTypeChange={handleFeeTypeChange}
          />
        );

      default:
        return null;
    }
  }, [
    activeTab,
    withdrawal,
    isLoadingWithdrawal,
    isRefetchingWithdrawal,
    withdrawalsPage,
    handleWithdrawalsPageChange,
    transactions,
    isLoadingTransactions,
    isRefetchingTransactions,
    transactionsPage,
    handleTransactionsPageChange,
    transactionsQuery,
    setTransactionsQuery,
    transactionsStatus,
    setTransactionsStatus,
    dateRange,
    handleDateRangeChange,
    taxes,
    isLoadingTaxes,
    feeType,
    handleFeeTypeChange,
    canViewWithdrawals,
    canViewStatement,
    canViewForecast,
    canViewFees,
  ]);

  return (
    <LayoutInside items={breadcrumbItems}>
      <CustomSheet
        open={canRequestWithdrawal && modals['withdrawal-form']}
        onClose={() => closeModal('withdrawal-form')}
      >
        <WithdrawalForm
          key={`withdrawal-main-${currency}`}
          enabled={Boolean(modals['withdrawal-form'])}
          availableBalance={
            retention?.find((item) => item.type === 'available_balance_withdrawal')
              ?.total_formatted ?? ''
          }
          availableBalanceCents={Math.round(
            Number(
              retention?.find((item) => item.type === 'available_balance_withdrawal')
                ?.total ?? 0,
            ),
          )}
          currency={currency}
          onClose={() => closeModal('withdrawal-form')}
        />
      </CustomSheet>

      <CustomSheet
        open={canRequestAnticipation && modals['advance-form']}
        onClose={() => closeModal('advance-form')}
      >
        <AdvanceForm />
      </CustomSheet>

      <GenericModal
        modalKey='document_pending'
        title={t('documentPending.title')}
        description={t('documentPending.description')}
        confirmButtonText={t('documentPending.confirmButtonText')}
        cancelButtonText={t('documentPending.cancelButtonText')}
        onConfirm={() => navigate('/my-account')}
        icon={<Sucess />}
      />

      <GenericModal
        modalKey='withdrawal-registered'
        title={t('withdrawalRegistered.title')}
        description={`${t('withdrawalRegistered.line1')}\n\n${t(
          'withdrawalRegistered.line2'
        )}\n\n${t('withdrawalRegistered.line3')}`}
        confirmButtonText={t('withdrawalRegistered.confirmButtonText')}
        cancelButtonText={t('withdrawalRegistered.confirmButtonText')}
        icon={<Sucess />}
        hideImage
        hideCancelButton
      />

      <ForecastHowItWorksModal />

      <PendingReceivablesModal
        currency={currency}
        onSeen={() => setLocalSeenHack((p) => p + 1)}
      />

      <div className='space-y-6'>
        <WalletCards
          {...walletData}
          canViewAvailable={canViewAvailable}
          canViewToRelease={canViewToRelease}
          canViewRetention={canViewRetention}
          canViewTotalWithdrawn={canViewTotalWithdrawn}
          canRequestWithdrawal={canRequestWithdrawal}
          canRequestAnticipation={canRequestAnticipation}
        />

        <div className='flex flex-col md:flex-row gap-4 items-start md:items-center justify-between'>
          <Tabs value={activeTab} className='hidden sm:block'>
            <TabsList className='bg-[#18181B] w-fit rounded-full'>
              {tabs.map(({ key, label }) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={
                    activeTab === key
                      ? 'bg-[#323235] rounded-full'
                      : 'rounded-full'
                  }
                  onClick={() => handleTabChange(key)}
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className='block sm:hidden'>
            <Select onValueChange={handleTabChange} value={activeTab}>
              <SelectTrigger className='bg-[#18181B] text-white'>
                <SelectValue placeholder={t('selectAnOption')} />
              </SelectTrigger>
              <SelectContent className='bg-[#18181B] text-white'>
                {tabs.map(({ key, label }) => (
                  <SelectItem
                    key={key}
                    value={key}
                    className='hover:bg-[#323235]'
                  >
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {activeTab === WALLET_TABS.FORECAST && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openModal(FORECAST_HOW_IT_WORKS_MODAL_KEY)}
              >
                <HelpCircle className="h-4 w-4" />
                {t('forecastHowItWorks.button')}
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => openModal(PENDING_RECEIVABLES_MODAL_KEY)}
                className={
                  !hasSeenReceivables && overdueTotalCount > 0
                    ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 hover:text-amber-300 border border-amber-500/30'
                    : ''
                }
              >
                {!hasSeenReceivables && overdueTotalCount > 0 ? (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    {t('pendingReceivables.button')}
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full bg-amber-500 text-zinc-900">
                      {overdueTotalCount}
                    </span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    {t('pendingReceivables.button')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>

        <div className='mt-5'>{renderTabContent}</div>
      </div>
    </LayoutInside>
  );
}
